import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config/api';

const API = API_BASE_URL;

/**
 * ContactPickerModal — shown between campaign selection and enrollment.
 * Lets users pick which email/phone to use for each candidate.
 * Preferred contacts are pre-selected.
 */
const ContactPickerModal = ({ candidateIds, campaignId, campaignName, pipelineId, onClose, onEnrolled, inline = false, onBack = null }) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [selections, setSelections] = useState({}); // { candidateId: { email, phone } }

  const token = localStorage.getItem('authToken');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    loadContacts();
  }, []);

  const loadContacts = async () => {
    try {
      const res = await fetch(`${API}/api/outreach/candidates/contacts`, {
        method: 'POST', headers,
        body: JSON.stringify({ candidate_ids: candidateIds })
      });
      if (res.ok) {
        const data = await res.json();
        setContacts(data);
        // Pre-select preferred contacts
        const initial = {};
        for (const c of data) {
          initial[c.id] = {
            email: c.preferred_email || '',
            phone: c.preferred_phone || ''
          };
        }
        setSelections(initial);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const updateSelection = (candidateId, field, value) => {
    setSelections(prev => ({
      ...prev,
      [candidateId]: { ...prev[candidateId], [field]: value }
    }));
  };

  const enroll = async () => {
    setEnrolling(true);
    try {
      const res = await fetch(`${API}/api/outreach/campaigns/${campaignId}/enroll`, {
        method: 'POST', headers,
        body: JSON.stringify({
          candidate_ids: candidateIds,
          pipeline_id: pipelineId || null,
          contacts: selections
        })
      });
      if (res.ok) {
        const result = await res.json();
        onEnrolled?.(result);
        onClose();
      } else {
        const err = await res.json();
        alert(err.error || 'Enrollment failed');
      }
    } catch (e) { console.error(e); alert('Enrollment failed'); }
    finally { setEnrolling(false); }
  };

  const candidatesWithoutEmail = contacts.filter(c => !selections[c.id]?.email);

  // Inline mode — renders inside a sidebar panel instead of a floating
  // modal. Same fetch, same selections state, same enroll call. Only
  // the outer chrome differs (compact header + stacked footer, no fixed
  // overlay). `onBack` (optional) becomes the secondary button and
  // takes the caller back to the previous sidebar step.
  if (inline) {
    return (
      <div className="flex-1 flex flex-col overflow-hidden bg-white">
        <div className="px-3 py-2 border-b flex items-center justify-between flex-shrink-0">
          <div className="min-w-0">
            <h3 className="text-xs font-semibold text-gray-900 truncate">Confirm contact info</h3>
            <p className="text-[10px] text-gray-500 truncate">
              For <span className="font-medium text-indigo-600">{campaignName}</span>
            </p>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-3">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            </div>
          ) : contacts.length === 0 ? (
            <p className="text-xs text-gray-500 text-center py-6">No candidates found.</p>
          ) : (
            <div className="space-y-3">
              {contacts.map(c => (
                <div key={c.id} className="border rounded p-2.5">
                  <div className="font-medium text-xs text-gray-900 mb-2 truncate">{c.name}</div>
                  <div className="space-y-2">
                    <div>
                      <label className="block text-[9px] text-gray-500 uppercase tracking-wide mb-0.5">Email</label>
                      {c.emails.length > 0 ? (
                        <select value={selections[c.id]?.email || ''}
                          onChange={(e) => updateSelection(c.id, 'email', e.target.value)}
                          className="w-full px-2 py-1 text-xs border rounded bg-white">
                          {c.emails.map((e, i) => (
                            <option key={i} value={e.value}>
                              {e.value}{e.type ? ` (${e.type})` : ''}{e.is_primary ? ' ★' : ''}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <div className="px-2 py-1 text-[11px] text-amber-600 bg-amber-50 border border-amber-200 rounded">
                          No email on file
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-[9px] text-gray-500 uppercase tracking-wide mb-0.5">Phone</label>
                      {c.phones.length > 0 ? (
                        <select value={selections[c.id]?.phone || ''}
                          onChange={(e) => updateSelection(c.id, 'phone', e.target.value)}
                          className="w-full px-2 py-1 text-xs border rounded bg-white">
                          <option value="">None</option>
                          {c.phones.map((p, i) => (
                            <option key={i} value={p.value}>
                              {p.value}{p.type ? ` (${p.type})` : ''}{p.is_primary ? ' ★' : ''}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <div className="px-2 py-1 text-[11px] text-gray-400 bg-gray-50 border rounded">
                          No phone on file
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="px-3 py-2 border-t bg-white flex-shrink-0">
          {contacts.length > 0 && (
            <div className="text-[10px] text-gray-500 mb-2">
              {contacts.length} candidate{contacts.length !== 1 ? 's' : ''}
              {candidatesWithoutEmail.length > 0 && (
                <span className="text-amber-600 ml-1">
                  · {candidatesWithoutEmail.length} without email
                </span>
              )}
            </div>
          )}
          <div className="flex gap-2">
            <button
              onClick={onBack || onClose}
              className="flex-1 px-3 py-1.5 text-xs border rounded hover:bg-gray-50"
            >
              {onBack ? 'Back' : 'Cancel'}
            </button>
            <button
              onClick={enroll}
              disabled={enrolling || loading || contacts.length === 0}
              className="flex-1 px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 font-medium"
            >
              {enrolling ? 'Enrolling…' : 'Enroll'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="px-5 py-4 border-b flex items-center justify-between flex-shrink-0">
          <div>
            <h3 className="font-semibold text-gray-900">Confirm Contact Info</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Select which email and phone to use for each candidate in <span className="font-medium text-indigo-600">{campaignName}</span>
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-5">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : contacts.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8">No candidates found.</p>
          ) : (
            <div className="space-y-4">
              {contacts.map(c => (
                <div key={c.id} className="border rounded-lg p-4">
                  <div className="font-medium text-sm text-gray-900 mb-2">{c.name}</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {/* Email selection */}
                    <div>
                      <label className="block text-[10px] text-gray-500 uppercase tracking-wide mb-1">Email</label>
                      {c.emails.length > 0 ? (
                        <select value={selections[c.id]?.email || ''}
                          onChange={(e) => updateSelection(c.id, 'email', e.target.value)}
                          className="w-full px-2.5 py-1.5 text-sm border rounded-lg bg-white">
                          {c.emails.map((e, i) => (
                            <option key={i} value={e.value}>
                              {e.value}{e.type ? ` (${e.type})` : ''}{e.is_primary ? ' ★' : ''}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <div className="px-2.5 py-1.5 text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-lg">
                          No email on file
                        </div>
                      )}
                    </div>
                    {/* Phone selection */}
                    <div>
                      <label className="block text-[10px] text-gray-500 uppercase tracking-wide mb-1">Phone</label>
                      {c.phones.length > 0 ? (
                        <select value={selections[c.id]?.phone || ''}
                          onChange={(e) => updateSelection(c.id, 'phone', e.target.value)}
                          className="w-full px-2.5 py-1.5 text-sm border rounded-lg bg-white">
                          <option value="">None</option>
                          {c.phones.map((p, i) => (
                            <option key={i} value={p.value}>
                              {p.value}{p.type ? ` (${p.type})` : ''}{p.is_primary ? ' ★' : ''}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <div className="px-2.5 py-1.5 text-xs text-gray-400 bg-gray-50 border rounded-lg">
                          No phone on file
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t bg-gray-50 rounded-b-xl flex items-center justify-between flex-shrink-0">
          <div className="text-xs text-gray-500">
            {contacts.length} candidate{contacts.length !== 1 ? 's' : ''}
            {candidatesWithoutEmail.length > 0 && (
              <span className="text-amber-600 ml-2">
                {candidatesWithoutEmail.length} without email
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border rounded-lg hover:bg-white">
              Cancel
            </button>
            <button onClick={enroll} disabled={enrolling || loading}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium">
              {enrolling ? 'Enrolling...' : `Enroll ${contacts.length} Candidate${contacts.length !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPickerModal;
